import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone';
import IconClock from '@spothero/icons/clock';
import IconExclamationCircle from '@spothero/icons/exclamation-circle';
import Button from '@spothero/ui/button';
import HorizontalRule from '@spothero/ui/horizontal-rule';
import DateUtils from 'utils/date';
import CancellationInfo from 'common/cancellation-info/CancellationInfo';
import Card from 'common/card/Card';
import CardContentPanel from 'common/card/CardContentPanel';
import PaymentMethodsIcons from 'common/payment-methods-icons/PaymentMethodsIcons';
import ParkingDateTimePanel from './ParkingDateTimePanel';
import ReviewPurchase from 'checkout/summary/ReviewPurchase';
import AvailableInventory from './AvailableInventory';

export default class AirportPurchaseInformation extends Component {
    static propTypes = {
        facility: PropTypes.object,
        isFetchingRates: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        availableTransientInventory: PropTypes.number,
        selectedRate: PropTypes.object,
        fetchRates: PropTypes.func.isRequired,
        isAirportRateUnavailable: PropTypes.bool,
        onCheckoutClick: PropTypes.func,
        pushTo: PropTypes.func,
        updateSelectedSpot: PropTypes.func,
        isRedesign: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        const {isFetchingRates, starts, ends} = props;
        const startsMoment = starts ? moment(starts) : null;
        const endsMoment = ends ? moment(ends) : null;
        let isEditingDateTime = true;

        if (startsMoment && endsMoment && startsMoment.isBefore(endsMoment)) {
            isEditingDateTime = false;
        }

        this._DATE_TIME_FORMAT = 'MMM D [at] h:mma';

        this.state = {
            prevIsFetchingRates: isFetchingRates,
            isEditingDateTime,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {isFetchingRates} = nextProps;

        if (prevState.prevIsFetchingRates && !isFetchingRates) {
            return {
                prevIsFetchingRates: isFetchingRates,
                isEditingDateTime: false,
            };
        }

        return null;
    }

    _onDateTimeSearchClick = ({starts, ends}) => {
        const {facility, fetchRates} = this.props;
        const {id: spotId} = facility;

        fetchRates({
            spotId,
            starts,
            ends,
            airport: true,
            meta: {
                dateTimeChanged: true,
            },
        });
    };

    _onDateTimeEditClick = () => {
        this.setState({
            isEditingDateTime: true,
        });
    };

    _onViewOtherOptionsClick = () => {
        const {
            facility: {
                airport: {
                    details: {url},
                },
            },
            starts,
            ends,
            pushTo,
            updateSelectedSpot,
        } = this.props;
        let searchUrl = `/${url}`;

        if (starts && ends) {
            searchUrl += `?airport=true&starts=${starts}&ends=${ends}`;
        }

        updateSelectedSpot();
        pushTo(searchUrl);
    };

    _renderPanelPreview(section) {
        const {starts, ends, isRedesign} = this.props;
        const {isEditingDateTime} = this.state;
        const dateTimeFormatted = isRedesign
            ? DateUtils.getDateTimeSummary({
                  startDateTimeObj: moment(starts),
                  endDateTimeObj: moment(ends),
                  isMonthly: false,
              })
            : `${moment(starts).format(this._DATE_TIME_FORMAT)} - ${moment(
                  ends
              ).format(this._DATE_TIME_FORMAT)}`;

        switch (section) {
            case 'datetime':
                if (!isEditingDateTime) {
                    return (
                        <span className="CardContentPanel-preview-datetime">
                            {dateTimeFormatted}
                        </span>
                    );
                } else {
                    return null;
                }
        }
    }

    _renderDateTimeEdit() {
        return (
            <Button
                className="CardContentPanel-title-actions"
                onClick={this._onDateTimeEditClick}
            >
                Edit
            </Button>
        );
    }

    render() {
        const {
            isFetchingRates,
            isAirportRateUnavailable,
            starts,
            ends,
            facility: {cancellation_allowed: cancellationAllowed},
            selectedRate,
            availableTransientInventory,
            onCheckoutClick,
            isRedesign,
        } = this.props;
        const {isEditingDateTime} = this.state;
        const cardClasses = classNames('AirportPurchaseInformation', {
            'AirportPurchaseInformation-redesign': isRedesign,
        });
        const datetimePreview = this._renderPanelPreview('datetime');
        const dateTimePanelClasses = classNames('Panel-date-time', {
            'Panel-completed': !isEditingDateTime,
        });
        const availabilityMessage =
            availableTransientInventory === 0
                ? 'No spots left.'
                : `Only ${availableTransientInventory} spots left.`;

        return (
            <Card
                className={cardClasses}
                heading={isRedesign ? null : 'Summary'}
            >
                {!isRedesign && availableTransientInventory <= 5 && (
                    <Fragment>
                        <div className="AirportPurchaseInformation-low-inventory">
                            <IconClock />
                            <span>{availabilityMessage}</span>
                        </div>
                        <HorizontalRule />
                    </Fragment>
                )}
                <CardContentPanel
                    className={dateTimePanelClasses}
                    title={
                        isRedesign ? 'Reservation Time' : 'Parking Date/Time'
                    }
                    additionalTitleMarkup={
                        !isEditingDateTime ? this._renderDateTimeEdit() : null
                    }
                    preview={datetimePreview}
                    isOpen={isEditingDateTime}
                    showContentOnPrint={false}
                >
                    <ParkingDateTimePanel
                        isFetchingRates={isFetchingRates}
                        starts={starts}
                        ends={ends}
                        onSearchClick={this._onDateTimeSearchClick}
                    />
                </CardContentPanel>
                {isRedesign && (
                    <AvailableInventory
                        availableTransientInventory={
                            availableTransientInventory
                        }
                    />
                )}
                {selectedRate && (
                    <ReviewPurchase showTaxesAndFeesMessage={isRedesign} />
                )}
                <Button
                    className="AirportPurchaseInformation-book-now"
                    color="primary"
                    disabled={!selectedRate}
                    onClick={onCheckoutClick}
                    block
                >
                    Book Now
                </Button>
                {isRedesign && <PaymentMethodsIcons showAppleAndGooglePay />}
                <CancellationInfo
                    className="AirportPurchaseInformation-no-cancellation"
                    cancellationAllowed={cancellationAllowed}
                    showFreeCancellation={isRedesign}
                />
                {isAirportRateUnavailable && (
                    <div className="AirportPurchaseInformation-unavailable">
                        <IconExclamationCircle />
                        <p>
                            We&apos;re sorry, this spot isn&apos;t available at
                            the selected times.
                        </p>
                        <Button
                            color="primary"
                            block
                            onClick={this._onViewOtherOptionsClick}
                        >
                            View Other Options
                        </Button>
                    </div>
                )}
            </Card>
        );
    }
}
